import React, { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { DatePickerField } from '../../../../components/common/DatePickerField';
import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import { brisbaneWallClockToIso } from '../../../../utils/businessTime';
import {
  EDM_UI,
  browserOffsetDiffersFromBrisbane,
  formatBrisbaneDateTime,
  formatLocalTime,
} from '../../../../utils/edm';

/**
 * Schedule control. The user picks a Brisbane wall-clock date and time; the
 * instant sent to the server is `brisbaneWallClockToIso(date, time)`, so a
 * browser on another timezone cannot shift the Brisbane-day rule. There is
 * deliberately no datetime-local input (it is browser-local).
 */
const SchedulePanel: React.FC<{
  status: string;
  scheduledAt: string | null;
  recipients: number;
  senderComplete: boolean;
  busy: boolean;
  onSchedule: (iso: string) => Promise<void>;
}> = ({ status, scheduledAt, recipients, senderComplete, busy, onSchedule }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [confirming, setConfirming] = useState(false);
  const iso = date && time ? brisbaneWallClockToIso(date, time) : null;
  const inPast = iso !== null && new Date(iso).getTime() < Date.now();

  if (status !== 'frozen') {
    if (status === 'scheduled' && scheduledAt) {
      return (
        <div className={EDM_UI.card}>
          <h2 className={EDM_UI.heading}>
            <CalendarClock className={EDM_UI.headingIcon} /> Schedule
          </h2>
          <p className="text-sm text-gray-200">
            Scheduled for {formatBrisbaneDateTime(scheduledAt, true)} Brisbane time (AEST, UTC+10).
          </p>
        </div>
      );
    }
    return null;
  }

  const when = iso ? formatBrisbaneDateTime(iso, true) : '';
  return (
    <div className={EDM_UI.card}>
      <h2 className={EDM_UI.heading}>
        <CalendarClock className={EDM_UI.headingIcon} /> Schedule
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DatePickerField id="edm-schedule-date" label="Date (Brisbane)" value={date} onChange={setDate} />
        <div>
          <label htmlFor="edm-schedule-time" className={EDM_UI.label}>
            Time (Brisbane)
          </label>
          <input
            id="edm-schedule-time"
            type="time"
            step={900}
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className={EDM_UI.input}
          />
        </div>
      </div>
      {iso && (
        <p className="text-sm text-gray-300">
          Sends from {when} Brisbane time (AEST, UTC+10).
          {browserOffsetDiffersFromBrisbane(iso) && ` · ${formatLocalTime(iso)} your local time`}
        </p>
      )}
      {inPast && <p className="text-sm text-red-400">Pick a time in the future.</p>}
      <p className="text-xs text-gray-400">
        One campaign can start per Brisbane calendar day for your company, and at most 3 campaigns can be active at once.
        Large lists send gradually (about 5,000 emails an hour), so a big campaign can finish later in the day.
      </p>
      {!senderComplete && (
        <p className="text-sm text-amber-300">Complete your sender identity above before you can schedule.</p>
      )}
      <button
        type="button"
        className={EDM_UI.primaryButton}
        disabled={!iso || inPast || !senderComplete || busy}
        onClick={() => setConfirming(true)}
      >
        <CalendarClock className="h-4 w-4" /> Schedule
      </button>
      <ConfirmDialog
        isOpen={confirming && iso !== null}
        title="Schedule campaign"
        message={`Schedule ${recipients.toLocaleString('en-AU')} emails to start ${when} Brisbane time? You can pause or cancel until sending finishes.`}
        confirmLabel="Schedule"
        confirmVariant="primary"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          if (iso) void onSchedule(iso);
        }}
      />
    </div>
  );
};

export default SchedulePanel;
